import React, { useState, useCallback, useEffect } from 'react';
import { useSwipeable } from 'react-swipeable';
import { Page } from '../types';
import { SwipeDirection, SwipeOffset } from '../types/swipeable';

interface UseSwipeNavigationProps {
  pages: Page[];
  currentPageId: string;
  setCurrentPageId: (id: string) => void;
  onPageChange?: (pageIndex: number) => void;
}

export const useSwipeNavigation = ({
  pages,
  currentPageId,
  setCurrentPageId,
  onPageChange
}: UseSwipeNavigationProps) => {
  const [isPageChanging, setIsPageChanging] = useState(false);
  const [swipeProgress, setSwipeProgress] = useState(0);
  const [isSwipeActive, setIsSwipeActive] = useState(false);
  const [swipeOffset, setSwipeOffset] = useState<SwipeOffset>({ x: 0, y: 0 });
  const [swipeDirection, setSwipeDirection] = useState<SwipeDirection>(null);
  const [horizontalPageIndex, setHorizontalPageIndex] = useState(0);
  const [lastResetPageId, setLastResetPageId] = useState<string | null>(null);
  
  // PartialHorizontalSlideの状態をページごとに保持
  const [partialSlideStates, setPartialSlideStates] = useState<Record<string, {
    currentSlideIndex: number;
    offset: number;
    containerWidth: number;
  }>>({});

  // 現在のページを取得
  const currentPage = pages.find(page => page.id === currentPageId);

  // ページ間の距離を計算する関数
  const getPageDistance = (pageId1: string, pageId2: string): number => {
    const index1 = pages.findIndex(page => page.id === pageId1);
    const index2 = pages.findIndex(page => page.id === pageId2);
    return Math.abs(index1 - index2);
  };

  // ページが変わったら、隣接ページ移動時に横スワイプインデックスをリセット
  useEffect(() => {
    // 初回はリセットしない
    if (lastResetPageId === null) {
      setLastResetPageId(currentPageId);
      return;
    }
    
    const distance = getPageDistance(currentPageId, lastResetPageId);
    
    // 隣接ページ（距離1）に移動したらリセット
    if (distance === 1) {
      setHorizontalPageIndex(0);
      setLastResetPageId(currentPageId);
      
      // 移動元のページ（前のページ）のPartialHorizontalSlideをリセット
      // カスタムイベントでリセットを通知
      window.dispatchEvent(new CustomEvent('partialSlideReset', {
        detail: { pageId: lastResetPageId }
      }));
    }
  }, [currentPageId, lastResetPageId, pages]);

  // 列番号を取得するヘルパー関数
  const getColumnNumber = (column: string): number => {
    if (column.length === 1) {
      return column.charCodeAt(0) - 'A'.charCodeAt(0) + 1;
    }
    const first = column.charCodeAt(0) - 'A'.charCodeAt(0) + 1;
    const second = column.charCodeAt(1) - 'A'.charCodeAt(0) + 1;
    return 26 + (first - 1) * 26 + second;
  };

  // 各ページの隣接ページを計算（完全に独立した管理）
  const getAdjacentPages = React.useMemo(() => {
    const adjacentMap: { [key: string]: { up?: string; down?: string; left?: string; right?: string } } = {};
    
    pages.forEach(page => {
      const row = page.row;
      const column = page.column;
      
      // 縦方向（上下）の隣接ページ - 常にA列
      const upPage = pages.find(p => p.row === row - 1 && p.column === 'A');
      const downPage = pages.find(p => p.row === row + 1 && p.column === 'A');
      
      // 横方向（左右）の隣接ページ - 同じ行
      const getNextColumn = (col: string): string => {
        if (col.length === 1) {
          const nextCharCode = col.charCodeAt(0) + 1;
          if (nextCharCode <= 'Z'.charCodeAt(0)) {
            return String.fromCharCode(nextCharCode);
          }
          return 'AA';
        }
        const first = col.charCodeAt(0);
        const second = col.charCodeAt(1);
        if (second < 'Z'.charCodeAt(0)) {
          return String.fromCharCode(first) + String.fromCharCode(second + 1);
        }
        return String.fromCharCode(first + 1) + 'A';
      };

      const getPrevColumn = (col: string): string => {
        if (col === 'A') return 'A';
        if (col.length === 1) {
          const prevCharCode = col.charCodeAt(0) - 1;
          return String.fromCharCode(prevCharCode);
        }
        const first = col.charCodeAt(0);
        const second = col.charCodeAt(1);
        if (second > 'A'.charCodeAt(0)) {
          return String.fromCharCode(first) + String.fromCharCode(second - 1);
        }
        return String.fromCharCode(first - 1) + 'Z';
      };

      const leftPage = pages.find(p => p.row === row && p.column === getPrevColumn(column));
      const rightPage = pages.find(p => p.row === row && p.column === getNextColumn(column));
      
      adjacentMap[page.id] = {
        up: upPage?.id,
        down: downPage?.id,
        left: leftPage?.id,
        right: rightPage?.id
      };
    });
    
    return adjacentMap;
  }, [pages]);

  // 現在のページとその隣接ページを取得
  const getCurrentPageWithAdjacents = React.useMemo(() => {
    if (!currentPage) return { current: null, adjacents: [] };
    
    const adjacentPages = getAdjacentPages[currentPage.id];
    const adjacents = [
      adjacentPages.up ? pages.find(p => p.id === adjacentPages.up) : null,
      adjacentPages.down ? pages.find(p => p.id === adjacentPages.down) : null,
      adjacentPages.left ? pages.find(p => p.id === adjacentPages.left) : null,
      adjacentPages.right ? pages.find(p => p.id === adjacentPages.right) : null
    ].filter(Boolean);
    
    return { current: currentPage, adjacents };
  }, [currentPage, getAdjacentPages, pages]);

  // 縦スワイプ（行移動）- 純粋な縦移動のみ
  const goToNextRow = useCallback(() => {
    if (!isPageChanging && currentPage) {
      const adjacentPages = getAdjacentPages[currentPage.id];
      if (adjacentPages?.down) {
        setIsPageChanging(true);
        setCurrentPageId(adjacentPages.down);
        const newPageIndex = pages.findIndex(p => p.id === adjacentPages.down);
        onPageChange?.(newPageIndex);
        
        setTimeout(() => {
          setIsPageChanging(false);
          setSwipeDirection(null);
        }, 800);
      }
    }
  }, [currentPage, getAdjacentPages, onPageChange, isPageChanging, pages, setCurrentPageId]);

  const goToPreviousRow = useCallback(() => {
    if (!isPageChanging && currentPage) {
      const adjacentPages = getAdjacentPages[currentPage.id];
      if (adjacentPages?.up) {
        setIsPageChanging(true);
        setCurrentPageId(adjacentPages.up);
        const newPageIndex = pages.findIndex(p => p.id === adjacentPages.up);
        onPageChange?.(newPageIndex);
        
        setTimeout(() => {
          setIsPageChanging(false);
          setSwipeDirection(null);
        }, 800);
      }
    }
  }, [currentPage, getAdjacentPages, onPageChange, isPageChanging, pages, setCurrentPageId]);

  // 横スワイプ（列移動）- 純粋な横移動のみ
  const goToNextColumn = useCallback(() => {
    if (!isPageChanging && currentPage) {
      // モック用の横スワイプページの場合は、サブページを移動
      // 注: horizontalSlidesがある場合は除外（実際のサイトの横スライド機能）
      if (currentPage.isHorizontal && 
          currentPage.subPageIds && 
          currentPage.subPageIds.length > 0 &&
          !currentPage.horizontalSlides) {
        // 無限スクロール対応：インデックスを無限に増やす
        setHorizontalPageIndex(prev => prev + 1);
        return;
      }
      
      const adjacentPages = getAdjacentPages[currentPage.id];
      if (adjacentPages?.right) {
        setIsPageChanging(true);
        setCurrentPageId(adjacentPages.right);
        const newPageIndex = pages.findIndex(p => p.id === adjacentPages.right);
        onPageChange?.(newPageIndex);
        
        setTimeout(() => {
          setIsPageChanging(false);
          setSwipeDirection(null);
        }, 800);
      }
    }
  }, [currentPage, getAdjacentPages, onPageChange, isPageChanging, pages, setCurrentPageId]);

  const goToPreviousColumn = useCallback(() => {
    if (!isPageChanging && currentPage) {
      // モック用の横スワイプページの場合は、サブページを移動
      // 注: horizontalSlidesがある場合は除外（実際のサイトの横スライド機能）
      if (currentPage.isHorizontal && 
          currentPage.subPageIds && 
          currentPage.subPageIds.length > 0 &&
          !currentPage.horizontalSlides) {
        // 無限スクロール対応：インデックスを無限に減らす
        setHorizontalPageIndex(prev => prev - 1);
        return;
      }
      
      const adjacentPages = getAdjacentPages[currentPage.id];
      if (adjacentPages?.left) {
        setIsPageChanging(true);
        setCurrentPageId(adjacentPages.left);
        const newPageIndex = pages.findIndex(p => p.id === adjacentPages.left);
        onPageChange?.(newPageIndex);
        
        setTimeout(() => {
          setIsPageChanging(false);
          setSwipeDirection(null);
        }, 800);
      }
    }
  }, [currentPage, getAdjacentPages, onPageChange, isPageChanging, pages, setCurrentPageId]);

  const shouldIgnoreSwipeTarget = useCallback((target: EventTarget | null) => {
    const element = target as Element | null;
    if (!element) return false;

    return Boolean(
      element.closest('.partial-horizontal-slide-container') ||
      element.closest('a, button, input, textarea, select, label, summary, [role="button"], [data-no-swipe]')
    );
  }, []);

  const handlers = useSwipeable({
    onSwipedUp: (eventData) => {
      if (shouldIgnoreSwipeTarget(eventData.event?.target)) {
        setIsSwipeActive(false);
        setSwipeProgress(0);
        setSwipeOffset({ x: 0, y: 0 });
        setSwipeDirection(null);
        return;
      }
      if (swipeProgress > 0.3) {
        goToNextRow();
      }
      setIsSwipeActive(false);
      setSwipeProgress(0);
      setSwipeOffset({ x: 0, y: 0 });
      setSwipeDirection(null);
    },
    onSwipedDown: (eventData) => {
      if (shouldIgnoreSwipeTarget(eventData.event?.target)) {
        setIsSwipeActive(false);
        setSwipeProgress(0);
        setSwipeOffset({ x: 0, y: 0 });
        setSwipeDirection(null);
        return;
      }
      if (swipeProgress > 0.3) {
        goToPreviousRow();
      }
      setIsSwipeActive(false);
      setSwipeProgress(0);
      setSwipeOffset({ x: 0, y: 0 });
      setSwipeDirection(null);
    },
    onSwipedLeft: (eventData) => {
      if (shouldIgnoreSwipeTarget(eventData.event?.target)) {
        setIsSwipeActive(false);
        setSwipeProgress(0);
        setSwipeOffset({ x: 0, y: 0 });
        setSwipeDirection(null);
        return;
      }
      if (swipeProgress > 0.3) {
        goToNextColumn();
      }
      setIsSwipeActive(false);
      setSwipeProgress(0);
      setSwipeOffset({ x: 0, y: 0 });
      setSwipeDirection(null);
    },
    onSwipedRight: (eventData) => {
      if (shouldIgnoreSwipeTarget(eventData.event?.target)) {
        setIsSwipeActive(false);
        setSwipeProgress(0);
        setSwipeOffset({ x: 0, y: 0 });
        setSwipeDirection(null);
        return;
      }
      if (swipeProgress > 0.3) {
        goToPreviousColumn();
      }
      setIsSwipeActive(false);
      setSwipeProgress(0);
      setSwipeOffset({ x: 0, y: 0 });
      setSwipeDirection(null);
    },
    onSwiping: (eventData) => {
      if (shouldIgnoreSwipeTarget(eventData.event?.target)) {
        return;
      }
      
      const deltaX = eventData.deltaX || 0;
      const deltaY = eventData.deltaY || 0;
      
      // スワイプ進行度を計算
      const progress = Math.min(Math.abs(deltaX || deltaY || 0) / 50, 1);
      setSwipeProgress(progress);
      setIsSwipeActive(progress > 0.05);
    },
    onSwipeStart: (eventData) => {
      if (shouldIgnoreSwipeTarget(eventData.event?.target)) {
        return;
      }
      setIsSwipeActive(true);
    },
    onSwiped: (eventData) => {
      if (shouldIgnoreSwipeTarget(eventData.event?.target)) {
        setIsSwipeActive(false);
        setSwipeProgress(0);
        setSwipeOffset({ x: 0, y: 0 });
        setSwipeDirection(null);
        return;
      }
      // スワイプが完了したが、閾値を超えていない場合は元の位置に戻す
      if (swipeProgress <= 0.3) {
        setIsSwipeActive(false);
        setSwipeProgress(0);
        setSwipeOffset({ x: 0, y: 0 });
        setSwipeDirection(null);
      } else {
        // 閾値を超えた場合はページ切り替えを実行
        if (swipeDirection === 'up') {
          goToNextRow();
        } else if (swipeDirection === 'down') {
          goToPreviousRow();
        } else if (swipeDirection === 'left') {
          goToNextColumn();
        } else if (swipeDirection === 'right') {
          goToPreviousColumn();
        }
        setIsSwipeActive(false);
        setSwipeProgress(0);
        setSwipeOffset({ x: 0, y: 0 });
        setSwipeDirection(null);
      }
    },
    delta: 10,
    preventScrollOnSwipe: false,
    trackMouse: true,
    trackTouch: true
  });

  // マウスホイール・マウスパッドジェスチャー対応（スロットリング付き）
  useEffect(() => {
    let wheelTimeout: NodeJS.Timeout | null = null;
    
    const handleWheel = (event: WheelEvent) => {
      // 既にページ変更中の場合は無視
      if (isPageChanging) {
        event.preventDefault();
        return;
      }
      
      // 連続したホイールイベントを制限
      if (wheelTimeout) {
        event.preventDefault();
        return;
      }
      
      // マウスホイールの縦スクロール
      if (Math.abs(event.deltaY) > Math.abs(event.deltaX)) {
        if (event.deltaY > 30) {
          goToNextRow();
          wheelTimeout = setTimeout(() => {
            wheelTimeout = null;
          }, 100);
        } else if (event.deltaY < -30) {
          goToPreviousRow();
          wheelTimeout = setTimeout(() => {
            wheelTimeout = null;
          }, 100);
        }
      }
      // マウスパッドの横スワイプ
      else if (Math.abs(event.deltaX) > 30) {
        if (event.deltaX > 0) {
          goToNextColumn();
          wheelTimeout = setTimeout(() => {
            wheelTimeout = null;
          }, 100);
        } else {
          goToPreviousColumn();
          wheelTimeout = setTimeout(() => {
            wheelTimeout = null;
          }, 100);
        }
      }
      
      event.preventDefault();
    };

    const handleKeyPress = (event: KeyboardEvent) => {
      // ページ変更中は無視
      if (isPageChanging) return;
      
      switch (event.key) {
        case 'ArrowUp':
          goToPreviousRow();
          break;
        case 'ArrowDown':
          goToNextRow();
          break;
        case 'ArrowLeft':
          goToPreviousColumn();
          break;
        case 'ArrowRight':
          goToNextColumn();
          break;
      }
    };

    window.addEventListener('wheel', handleWheel, { passive: false });
    window.addEventListener('keydown', handleKeyPress);
    
    return () => {
      window.removeEventListener('wheel', handleWheel);
      window.removeEventListener('keydown', handleKeyPress);
      if (wheelTimeout) {
        clearTimeout(wheelTimeout);
      }
    };
  }, [goToPreviousRow, goToNextRow, goToPreviousColumn, goToNextColumn, isPageChanging]);

  // PartialHorizontalSlideの状態を取得・更新する関数
  const getPartialSlideState = useCallback((pageId: string) => {
    return partialSlideStates[pageId] || {
      currentSlideIndex: 0,
      offset: 0,
      containerWidth: 0
    };
  }, [partialSlideStates]);

  const updatePartialSlideState = useCallback((pageId: string, state: Partial<{
    currentSlideIndex: number;
    offset: number;
    containerWidth: number;
  }>) => {
    setPartialSlideStates(prev => ({
      ...prev,
      [pageId]: {
        ...(prev[pageId] || {
          currentSlideIndex: 0,
          offset: 0,
          containerWidth: 0
        }),
        ...state
      }
    }));
  }, []);

  return {
    handlers,
    currentPage,
    getCurrentPageWithAdjacents,
    getAdjacentPages,
    getColumnNumber,
    isPageChanging,
    swipeProgress,
    isSwipeActive,
    swipeOffset,
    swipeDirection,
    goToNextRow,
    goToPreviousRow,
    goToNextColumn,
    goToPreviousColumn,
    horizontalPageIndex,
    getPartialSlideState,
    updatePartialSlideState
  };
};
